'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface CalculatorResult {
  monthlySavings: number;
  yearlySavings: number;
  roi: number;
  paybackMonths: number;
}

const formatCurrency = (value: number): string => {
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const ROICalculator = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [employees, setEmployees] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(35);
  const [hoursPerWeek, setHoursPerWeek] = useState(5);
  const [result, setResult] = useState<CalculatorResult | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      calculateROI();
    }
  }, [employees, hourlyRate, hoursPerWeek, isHydrated]);

  const calculateROI = () => {
    const weeklyHoursSaved = employees * hoursPerWeek;
    const monthlySavings = weeklyHoursSaved * 4.33 * hourlyRate;
    const yearlySavings = monthlySavings * 12;
    const investmentCost = 2500;
    const roi = ((yearlySavings - investmentCost) / investmentCost) * 100;
    const paybackMonths = investmentCost / monthlySavings;

    setResult({
      monthlySavings,
      yearlySavings,
      roi,
      paybackMonths
    });
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-b from-transparent to-primary/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
                Bereken uw ROI
              </h2>
              <p className="text-lg text-muted-foreground">
                Laden...
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-transparent to-primary/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Bereken uw ROI
            </h2>
            <p className="text-lg text-muted-foreground">
              Ontdek hoeveel u kunt besparen met AI-automatisering
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="glassmorphic rounded-2xl p-8">
              <h3 className="text-xl font-display font-bold text-foreground mb-6">
                Uw bedrijfsgegevens
              </h3>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Aantal medewerkers
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={employees}
                      onChange={(e) => setEmployees(Number(e.target.value))}
                      className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <span className="text-2xl font-bold text-primary w-16 text-right">
                      {employees}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Gemiddeld uurloon (€)
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="20"
                      max="100"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(Number(e.target.value))}
                      className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <span className="text-2xl font-bold text-primary w-16 text-right">
                      €{hourlyRate}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Uren per week te automatiseren
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="1"
                      max="20"
                      value={hoursPerWeek}
                      onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                      className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <span className="text-2xl font-bold text-primary w-16 text-right">
                      {hoursPerWeek}u
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="glassmorphic rounded-2xl p-8 shadow-silver-glow card-3d-glow">
              <h3 className="text-xl font-display font-bold text-foreground mb-6">
                Uw potentiële besparingen
              </h3>

              {result && (
                <div className="space-y-6">
                  <div className="p-6 rounded-xl bg-primary/10 border border-primary/20 shadow-orange-glow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Maandelijkse besparing</span>
                      <Icon name="TrendingUpIcon" size={20} className="text-primary" />
                    </div>
                    <p className="text-3xl font-display font-bold text-primary">
                      €{formatCurrency(result.monthlySavings)}
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-success/10 border border-success/20 shadow-green-glow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Jaarlijkse besparing</span>
                      <Icon name="ChartBarIcon" size={20} className="text-success" />
                    </div>
                    <p className="text-3xl font-display font-bold text-success glow-success">
                      €{formatCurrency(result.yearlySavings)}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-card border border-border shadow-silver">
                      <p className="text-sm text-muted-foreground mb-1">ROI</p>
                      <p className="text-2xl font-bold text-success">
                        {result.roi.toFixed(0)}%
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-card border border-border shadow-silver">
                      <p className="text-sm text-muted-foreground mb-1">Terugverdientijd</p>
                      <p className="text-2xl font-bold text-foreground">
                        {result.paybackMonths.toFixed(1)} mnd
                      </p>
                    </div>
                  </div>

                  <div className="pt-4">
                    <p className="text-xs text-muted-foreground text-center mb-4">
                      * Berekening gebaseerd op gemiddelde AI-implementatie kosten van €2.500
                    </p>
                    <Link
                      href="/contact"
                      className="block w-full py-3 px-6 rounded-lg font-semibold text-center bg-cta text-cta-foreground hover:bg-cta/90 transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover button-3d"
                    >
                      Start uw AI-transformatie
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;